import { discoveryOrder, type DiscoveryRow } from './order';
import { holdOrder, reorderAllowed, type QuietGate } from './reorder';
import type { DiscoveryBucket } from '../types';

/**
 * DISC-2 and DISC-7 — the Discovery pager's page set, and the **deferred-reorder engine**, pure.
 *
 * `order.ts` answers *what the order should be* (DISC-4). `reorder.ts` answers *when any order may
 * be applied* (DISC-7) — a question with a different owner and, since U-9b, a second caller, so it
 * moved out of this file rather than the locked board importing a gate from a module named for
 * Discovery (§17's scope rule). What is left here is the composition of the two for **this** surface:
 * DISC-2's page set, and the per-page target/hold decision. Keeping the **rules** in `domain/` and
 * only the timers in `ui/` is what makes DISC-7 provable in Node — the guarantee is a property of a
 * sequence of taps and evaluations, and a browser can demonstrate one instance of it at a time.
 *
 * ── DISC-7, and the two layers that satisfy it (design §15 DD-1) ─────────────
 *
 * > *"A reorder MUST NOT relocate any row during the window in which a follow-up tap could land on
 * > it: no row may change its rendered position within `REORDER_QUIET_MS` of any tap. A user
 * > double-tapping to record two observations MUST NOT increment a different hypothesis with the
 * > second tap."*
 *
 * - The **correctness** layer is not here. It is `data-hid` resolved at `pointerdown` (§5.1 step 1),
 *   which makes "the second tap cannot increment a different hypothesis" absolutely true rather than
 *   probabilistically true — no reorder, mid-animation included, can retarget a tap that has begun.
 * - The **aim** layer is `reorder.ts`'s `reorderAllowed`, consumed here: it keeps the row where the
 *   user last saw it. §17.1 requires the same guarantee "on Discovery **and** the locked board", and
 *   it is the same function on both.
 *
 * ── One deliberate divergence from §5.5's wording, and why ───────────────────
 *
 * §5.5 says the target order is applied *"when `now − lastTapTs ≥ REORDER_QUIET_MS` and no pointer
 * is down, **or immediately on page entry / return from background** (the user is demonstrably not
 * mid-gesture then)"*. DISC-7's second sentence carries no such exception — it is unconditional over
 * *any* tap — and a user who taps a row, swipes to the next page and swipes straight back inside the
 * second would, under the literal reading, find the row moved.
 *
 * So entry and `visibilitychange` are implemented as **triggers to re-evaluate this gate**, never as
 * a bypass. They stay load-bearing rather than becoming decoration: iOS throttles or suspends timers
 * in a backgrounded page, so a quiet-window timeout armed before the phone went in a pocket may
 * never fire, and the return-to-foreground evaluation is what settles the board. The strictly
 * stronger rule violates nothing (§19 F-5 already extends the guarantee rather than narrowing it),
 * and it is the reading DISC-7's own text supports.
 *
 * §19 F-32's concern — that near-continuous tapping suppresses DISC-4 exactly when it matters — is
 * bounded by construction: it requires taps at sustained sub-`REORDER_QUIET_MS` intervals, and the
 * first gap of a second settles every page at once.
 */

/**
 * DISC-2's four pages, in swipe order. *"Set pieces are not a page."*
 *
 * `satisfies` rather than a cast, so a fifth `DiscoveryBucket` breaks this build instead of
 * producing a bucket whose hypotheses render nowhere and are untappable — the exact failure mode
 * requirements revision 2 made `discoveryBucket` non-optional to prevent.
 */
export const DISCOVERY_PAGES = [
  'inPossession',
  'outOfPossession',
  'attTransition',
  'defTransition',
] as const satisfies readonly DiscoveryBucket[];

/** DISC-2's persistent 4-position page indicator reads this. */
export const DISCOVERY_PAGE_COUNT = DISCOVERY_PAGES.length;

/** Where a bucket sits in the pager. Total over `DiscoveryBucket` by the `satisfies` above. */
export function pageIndexOf(bucket: DiscoveryBucket): number {
  return DISCOVERY_PAGES.indexOf(bucket);
}

/** A row as the Discovery pager sees it: `order.ts`'s sort keys plus the page it belongs to. */
export interface DiscoveryPageRow extends DiscoveryRow {
  readonly discoveryBucket: DiscoveryBucket;
}

/**
 * DISC-4 — the **target** order for one page: that bucket's rows, descending by count.
 *
 * *"Count-driven reordering is permitted only in Discovery."* This is the only function in the
 * product that composes a bucket filter with `discoveryOrder`, and it is reachable only from the
 * Discovery surface; `lockedOrder` takes a parameter type with no count field, so the locked board
 * cannot call anything that would rank by one (LIVE-12, SC-6).
 *
 * "Target" is the operative word — nothing here applies it. See {@link reorderAllowed}.
 */
export function targetOrderFor<T extends DiscoveryPageRow>(
  rows: readonly T[],
  bucket: DiscoveryBucket,
): T[] {
  return discoveryOrder(rows.filter((row) => row.discoveryBucket === bucket));
}

/** The rendered order of every page, keyed by bucket. View state — derived, never stored. */
export type PageOrders = Readonly<Record<DiscoveryBucket, readonly string[]>>;

/** Before anything has rendered. {@link nextPageOrders} turns this into the target order. */
export function emptyPageOrders(): PageOrders {
  return { inPossession: [], outOfPossession: [], attTransition: [], defTransition: [] };
}

/**
 * The **one** entry point the Discovery surface calls to decide what order to render — on load,
 * after a commit, after a page change, after returning from background, and on every quiet-window
 * timeout. One path, so DISC-7 is a property of this function rather than of five call sites
 * agreeing.
 *
 * Gate open → the DISC-4 target order, every page at once. Gate shut → {@link holdOrder}, which
 * moves nothing and appends newcomers at the tail.
 *
 * Settling **all four pages** rather than only the visible one is deliberate: DISC-7's guarantee is
 * written over rows, not over pages, and a page settled lazily on entry would be a second code path
 * whose timing nothing tests. It is also cheap — four sorts of a few rows, at most once per second.
 */
export function nextPageOrders<T extends DiscoveryPageRow>(
  rows: readonly T[],
  previous: PageOrders,
  gate: QuietGate,
  now: number,
): PageOrders {
  return reorderAllowed(gate, now) ? settlePageOrders(rows) : holdPageOrders(rows, previous);
}

/** DISC-4 applied: every page in target order. */
export function settlePageOrders<T extends DiscoveryPageRow>(rows: readonly T[]): PageOrders {
  return {
    inPossession: targetIds(rows, 'inPossession'),
    outOfPossession: targetIds(rows, 'outOfPossession'),
    attTransition: targetIds(rows, 'attTransition'),
    defTransition: targetIds(rows, 'defTransition'),
  };
}

/** DISC-7 applied: every page holds its rendered positions, newcomers at the tail. */
export function holdPageOrders<T extends DiscoveryPageRow>(
  rows: readonly T[],
  previous: PageOrders,
): PageOrders {
  return {
    inPossession: holdOrder(previous.inPossession, targetIds(rows, 'inPossession')),
    outOfPossession: holdOrder(previous.outOfPossession, targetIds(rows, 'outOfPossession')),
    attTransition: holdOrder(previous.attTransition, targetIds(rows, 'attTransition')),
    defTransition: holdOrder(previous.defTransition, targetIds(rows, 'defTransition')),
  };
}

function targetIds<T extends DiscoveryPageRow>(
  rows: readonly T[],
  bucket: DiscoveryBucket,
): string[] {
  return targetOrderFor(rows, bucket).map((row) => row.id);
}
